import { useQuery } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { z } from 'zod'

import { readStorage, writeStorage } from './panel-state'

const changeEntrySchema = z.object({
  id: z.string().min(1),
  publicationApproved: z.boolean().optional(),
  title: z.string().min(1).max(120),
  area: z.string().min(1).max(60),
  publishedAt: z.iso.datetime(),
  summary: z.string().min(1).max(5_000).optional(),
  author: z.string().min(1).max(120).optional(),
  pullRequestUrl: z.url().optional(),
})

const changeFeedSchema = z.array(changeEntrySchema).max(5)

export type ChangeEntry = z.infer<typeof changeEntrySchema>

export function useChangeFeed() {
  return useQuery({
    queryKey: ['platform-change-feed'],
    queryFn: async ({ signal }) => {
      const response = await fetch('/changelog.json', { signal })
      if (!response.ok) throw new Error('Änderungsübersicht nicht erreichbar')
      return changeFeedSchema
        .parse(await response.json())
        .filter((entry) => entry.publicationApproved === true)
    },
    staleTime: 5 * 60_000,
  })
}

export function useNewChangeEntries(
  entries: ChangeEntry[],
  expanded: boolean,
  storageKey: string,
): { newEntryIds: ReadonlySet<string>; acknowledge: () => void } {
  const [visitBaseline, setVisitBaseline] = useState(() => readStorage(storageKey))
  const newestPublishedAt = entries[0]?.publishedAt
  const effectiveBaseline = isValidDate(visitBaseline) ? visitBaseline : newestPublishedAt

  useEffect(() => {
    if (!newestPublishedAt) return
    if (expanded || !isValidDate(visitBaseline)) {
      writeStorage(storageKey, newestPublishedAt)
    }
  }, [expanded, newestPublishedAt, storageKey, visitBaseline])

  const newEntryIds = useMemo(() => {
    if (!effectiveBaseline) return new Set<string>()
    const baseline = Date.parse(effectiveBaseline)
    return new Set(
      entries
        .filter((entry) => Date.parse(entry.publishedAt) > baseline)
        .map((entry) => entry.id),
    )
  }, [effectiveBaseline, entries])

  const acknowledge = useCallback(() => {
    if (!newestPublishedAt) return
    setVisitBaseline(newestPublishedAt)
    writeStorage(storageKey, newestPublishedAt)
  }, [newestPublishedAt, storageKey])

  return { newEntryIds, acknowledge }
}

export function formatChangeDate(value: string, now = new Date()): string {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''

  const formatter = new Intl.DateTimeFormat('de-DE', {
    timeZone: 'Europe/Berlin',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })
  if (formatter.format(date) === formatter.format(now)) return 'Heute'

  return new Intl.DateTimeFormat('de-DE', {
    timeZone: 'Europe/Berlin',
    day: '2-digit',
    month: 'short',
  }).format(date)
}

export function formatChangeDetailDate(value: string): string {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''

  return new Intl.DateTimeFormat('de-DE', {
    timeZone: 'Europe/Berlin',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  }).format(date)
}

function isValidDate(value: string | null): value is string {
  return value !== null && !Number.isNaN(Date.parse(value))
}
